import { useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { toast } from "react-toastify";
import {
  useLazyGetListOfBugsQuery,
  useUpdateBugDetailMutation,
  useUpdateBugIndexMutation,
} from "../../../../features/bughurd/api";
import {
  setLoadingStatus,
  setPagination,
  setHasMore,
  updateColumn,
  setSubGroupColumnData,
  setSubGroupLoadingState,
  updateSubGroupData,
  setScrollPosition,
} from "@/features/bughurd/slice";

const PAGE_LIMIT = 10;

export const useBoardData = () => {
  const dispatch = useAppDispatch();
  const [getBugsByStatus] = useLazyGetListOfBugsQuery();
  const [updateBug] = useUpdateBugDetailMutation();
  const [updateBugIndex] = useUpdateBugIndexMutation();

  const fetchInProgress = useRef<Record<string, boolean>>({});
  const previousTasksRef = useRef<Record<string, number>>({});
  const isDragInProgress = useRef<boolean>(false);
  const dragOperationInProgress = useRef<boolean>(false);
  const skipStatusRefetch = useRef<boolean>(false);
  const statusDataInitialized = useRef<boolean>(false);
  const viewChangeRef = useRef<boolean>(false);
  const lastFetchParams = useRef({
    search: "",
    tags: [] as string[],
    avatars: [] as string[],
    severities: [] as string[],
    statuses: [] as string[],
    createdBy: [] as string[],
    projects: [] as string[],
    dueDate: "",
    startDate: "",
    updatedDate: "",
    dueDateStart: "",
    dueDateEnd: "",
    startDateStart: "",
    startDateEnd: "",
    updatedAtStart: "",
    updatedAtEnd: "",
    groupBy: "Status",
    subGroupBy: "None",
    isRecurring: false,
    activeSprintIds: [] as string[],
    selectedSprintId: null as string | null,
  });
  const groupByChangeRef = useRef<string>("Status");
  const isGroupByChanging = useRef<boolean>(false);

  const resetSkipStatusRefetch = useCallback(() => {
    skipStatusRefetch.current = false;
  }, []);

  const setSkipStatusRefetch = useCallback((value: boolean) => {
    skipStatusRefetch.current = value;
  }, []);

  // Store scroll position before update
  const preserveScrollPosition = useCallback((columnId: string) => {
    const columnElement = document.querySelector(
      `[data-column-id="${columnId}"] [data-rbd-droppable-id="${columnId}"]`
    );
    if (columnElement) {
      dispatch(
        setScrollPosition({
          columnId,
          position: columnElement.scrollTop,
        })
      );
    }
  }, []);

  // Restore scroll position after update
  const restoreScrollPosition = useCallback(
    (scrollPositions: Record<string, number>) => (columnId: string) => {
      setTimeout(() => {
        const columnElement = document.querySelector(
          `[data-column-id="${columnId}"] [data-rbd-droppable-id="${columnId}"]`
        );
        if (columnElement && scrollPositions[columnId] !== undefined) {
          columnElement.scrollTop = scrollPositions[columnId];
        }
      }, 50);
    },
    []
  );

  return {
    getBugsByStatus,
    updateBug,
    updateBugIndex,
    fetchInProgress,
    previousTasksRef,
    isDragInProgress,
    dragOperationInProgress,
    skipStatusRefetch,
    statusDataInitialized,
    viewChangeRef,
    lastFetchParams,
    groupByChangeRef,
    isGroupByChanging,
    preserveScrollPosition,
    restoreScrollPosition,
    PAGE_LIMIT,
    resetSkipStatusRefetch,
    setSkipStatusRefetch,
  };
};
